import { newId, useSettings, type Settings } from './settings';

// Saved layouts ("looks"): how the screen is arranged and styled, without the content
// sources (photos, calendars, feeds, location), so a look can be applied on any screen.

export interface Look {
  layout: Settings['layout'];
  layoutPortrait: Settings['layoutPortrait'];
  theme: Settings['theme'];
  display: Settings['display'];
  customCss: string;
  clock: Settings['clock'];
  media: Pick<Settings['media'], 'transition' | 'fit' | 'overlayColor' | 'overlayOpacity' | 'intervalSec' | 'shuffle'>;
  widgets: { weather: boolean; calendar: boolean; lists: boolean; news: boolean };
  calendar: Pick<Settings['calendar'], 'view' | 'weekStartsOn' | 'days'>;
  news: Pick<Settings['news'], 'display' | 'speed' | 'cardIntervalSec'>;
  weather: Omit<Settings['weather'], 'enabled' | 'location'>;
}

export interface Preset {
  id: string;
  name: string;
  savedAt: string;
  look: Look;
}

export function captureLook(s: Settings): Look {
  const { enabled: _w, location: _l, ...weather } = s.weather;
  return {
    layout: s.layout,
    layoutPortrait: s.layoutPortrait,
    theme: s.theme,
    display: { ...s.display },
    customCss: s.customCss,
    clock: { ...s.clock },
    media: {
      transition: s.media.transition,
      fit: s.media.fit,
      overlayColor: s.media.overlayColor,
      overlayOpacity: s.media.overlayOpacity,
      intervalSec: s.media.intervalSec,
      shuffle: s.media.shuffle,
    },
    widgets: { weather: s.weather.enabled, calendar: s.calendar.enabled, lists: s.lists.enabled, news: s.news.enabled },
    calendar: { view: s.calendar.view, weekStartsOn: s.calendar.weekStartsOn, days: s.calendar.days },
    news: { display: s.news.display, speed: s.news.speed, cardIntervalSec: s.news.cardIntervalSec },
    weather,
  };
}

/** Applies a look on top of the current settings; content sources are left untouched. */
export function applyLook(look: Partial<Look>): void {
  const { update } = useSettings.getState();
  if (look.layout) update('layout', look.layout);
  if (look.layoutPortrait) update('layoutPortrait', look.layoutPortrait);
  if (look.theme) update('theme', look.theme);
  if (look.display) update('display', look.display);
  if (look.customCss !== undefined) update('customCss', look.customCss);
  if (look.clock) update('clock', look.clock);
  if (look.media) update('media', look.media);
  if (look.calendar || look.widgets) update('calendar', { ...look.calendar, ...(look.widgets ? { enabled: look.widgets.calendar } : {}) });
  if (look.news || look.widgets) update('news', { ...look.news, ...(look.widgets ? { enabled: look.widgets.news } : {}) });
  if (look.weather || look.widgets) update('weather', { ...look.weather, ...(look.widgets ? { enabled: look.widgets.weather } : {}) });
  if (look.widgets) update('lists', { enabled: look.widgets.lists });
}

export function savePreset(name: string): Preset {
  const s = useSettings.getState();
  const preset: Preset = { id: newId(), name: name.trim().slice(0, 60) || 'My layout', savedAt: new Date().toISOString(), look: captureLook(s) };
  s.update('presets', [...s.presets, preset]);
  return preset;
}

export function deletePreset(id: string): void {
  const s = useSettings.getState();
  s.update('presets', s.presets.filter((p) => p.id !== id));
}

export function renamePreset(id: string, name: string): void {
  const s = useSettings.getState();
  s.update('presets', s.presets.map((p) => (p.id === id ? { ...p, name: name.trim().slice(0, 60) || p.name } : p)));
}

/** Replaces a saved layout with the current look (keeps its name). */
export function overwritePreset(id: string): void {
  const s = useSettings.getState();
  const look = captureLook(s);
  s.update('presets', s.presets.map((p) => (p.id === id ? { ...p, look, savedAt: new Date().toISOString() } : p)));
}
